// src/apiClient.js
const API_BASE_URL = ''; // Adjust if your API has a base URL (e.g., 'https://api.good-call.app')

// Decode JWT to get expiry time (optional, for proactive refreshing)
const decodeJWT = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error decoding JWT:', e);
    return null;
  }
};

// Main API client
const apiClient = {
  // Function to set tokens (called after login or refresh)
  setTokens: (accessToken, refreshToken) => {
    apiClient.accessToken = accessToken;
    apiClient.refreshToken = refreshToken;
    // Update localStorage
    const userData = JSON.parse(localStorage.getItem('userData')) || {};
    localStorage.setItem(
      'userData',
      JSON.stringify({
        ...userData,
        access_token: accessToken,
        refresh_token: refreshToken,
      })
    );
  },

  // Function to clear tokens (called on logout)
  clearTokens: () => {
    apiClient.accessToken = null;
    apiClient.refreshToken = null;
    localStorage.removeItem('userData');
  },

  // Function to refresh tokens
  refreshTokens: async () => {
    if (!apiClient.refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${API_BASE_URL}/api/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiClient.refreshToken}`,
      },
    });

    if (!response.ok) {
      // If refresh fails, clear tokens and throw an error
      apiClient.clearTokens();
      throw new Error('Failed to refresh token');
    }

    const data = await response.json();
    const { access_token, refresh_token } = data;

    // Update tokens in the client and localStorage
    apiClient.setTokens(access_token, refresh_token);
    return access_token;
  },

  // Main request function
  request: async (url, options = {}) => {
    const headers = {
      ...options.headers,
    };

    // Add Authorization header if accessToken exists
    if (apiClient.accessToken) {
      headers['Authorization'] = `Bearer ${apiClient.accessToken}`;
    }

    // Prepare fetch options
    const fetchOptions = {
      ...options,
      headers,
    };

    // Handle FormData: set body directly and avoid setting Content-Type
    if (options.data instanceof FormData) {
      fetchOptions.body = options.data;
      // Log FormData entries for debugging
      for (const [key, value] of options.data.entries()) {
        console.log(`FormData entry: ${key}=${value}`);
      }
    } else {
      // For non-FormData, set Content-Type to JSON and stringify body
      headers['Content-Type'] = 'application/json';
      if (options.data) {
        fetchOptions.body = JSON.stringify(options.data);
      }
    }

    // Check if access token is about to expire
    if (apiClient.accessToken) {
      const decoded = decodeJWT(apiClient.accessToken);
      if (decoded && decoded.exp) {
        const expiryTime = decoded.exp * 1000;
        const currentTime = Date.now();
        const timeUntilExpiry = expiryTime - currentTime;

        if (timeUntilExpiry < 60 * 1000) {
          try {
            await apiClient.refreshTokens();
            headers['Authorization'] = `Bearer ${apiClient.accessToken}`;
          } catch (err) {
            throw err;
          }
        }
      }
    }

    // Make the initial request
    let response = await fetch(`${API_BASE_URL}${url}`, fetchOptions);

    // Handle 401 by refreshing token
    if (response.status === 401 && apiClient.refreshToken) {
      try {
        const newAccessToken = await apiClient.refreshTokens();
        headers['Authorization'] = `Bearer ${newAccessToken}`;
        response = await fetch(`${API_BASE_URL}${url}`, fetchOptions);
      } catch (err) {
        throw err;
      }
    }

    if (!response.ok) {
      let data;
      try {
        data = await response.json();
      } catch (e) {
        throw new Error('Failed to parse response');
      }
      throw new Error(data.message || 'Request failed');
    }

    return response.json();
  },
};

// Initialize tokens from localStorage
const storedUserData = JSON.parse(localStorage.getItem('userData'));
if (storedUserData) {
  apiClient.accessToken = storedUserData.access_token || null;
  apiClient.refreshToken = storedUserData.refresh_token || null;
}

export default apiClient;